import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button } from '@kcm/ui'
import { ErrorCode, isTerminalKycState } from '@kcm/contracts'
import { useTranslate } from '@tolgee/react'
import { useSite } from '../components/SiteProvider'
import { ApiError } from '../lib/api'
import { endpoints } from '../lib/endpoints'
import { KycOutcome } from '../components/KycOutcome'

/**
 * Where Didit sends the user back to (PLAN.md §15.1).
 *
 * The path is fixed — the API builds the callback URL from the site's own
 * configured host and this constant, never from the incoming request (findings
 * #1, #13) — so this screen must exist at exactly `/verify/return`.
 *
 * The race it exists to absorb: the user's browser comes back the instant they
 * finish, while the decision arrives separately over the webhook. For a few
 * seconds the server legitimately does not know the outcome yet. So this polls
 * rather than reading once, and says something honest in the meantime.
 */

/** Frequent enough to feel immediate, slow enough not to hammer the tiebreaker. */
const POLL_MS = 2_000
/** ~40s. Past that the webhook is late, and waiting is not the user's job. */
const MAX_POLLS = 20

export function VerifyReturn() {
  const navigate = useNavigate()
  const { t } = useTranslate()
  const [polls, setPolls] = useState(0)
  const gaveUp = polls >= MAX_POLLS

  const { data, error, isError, dataUpdatedAt } = useQuery({
    queryKey: ['kyc-status', 'return'],
    queryFn: ({ signal }) => endpoints.kyc.status(signal),
    // Nothing to retry: the poll below is already the retry, two seconds out,
    // and a backoff on top of it only delays the page.
    retry: false,
    refetchInterval: (query) => {
      // A 401 is the phone finishing a QR handoff. It has no cookie and is not
      // going to acquire one, so polling it is a request that can only ever
      // fail — from a device the user has been told to close.
      if (isHandoff(query.state.error)) return false

      const state = query.state.data?.state
      // in_review is terminal for this screen even though it is not terminal
      // for the application: no further polling will change what we can say.
      if (state && (isTerminalKycState(state) || state === 'in_review' || state === 'expired')) {
        return false
      }
      return gaveUp ? false : POLL_MS
    },
    refetchIntervalInBackground: false,
  })

  // Counts ticks rather than elapsed time so a backgrounded tab — the common
  // case on mobile, where the user switches away — is not timed out unfairly.
  //
  // Keyed on `dataUpdatedAt`, not on `data`: React Query's structural sharing
  // hands back the identical object when a poll returns an unchanged payload,
  // which is every poll while we are waiting. Comparing references would leave
  // the counter at 1 and the user on the spinner indefinitely.
  const seen = useRef(0)
  useEffect(() => {
    if (dataUpdatedAt && dataUpdatedAt !== seen.current) {
      seen.current = dataUpdatedAt
      setPolls((n) => n + 1)
    }
  }, [dataUpdatedAt])

  const state = data?.state

  // No session on this browser, which is the second device in a QR handoff:
  // the desktop scanned, the phone did the camera work, and the phone never
  // had the cookie. It cannot be shown the outcome — it has not proven it owns
  // the application, and finding #2 is exactly what happens when a funnel
  // decides otherwise — but it should not be left staring at an error either.
  if (isHandoff(error)) {
    return <HandoffComplete />
  }

  if (state && state !== 'not_started' && state !== 'in_progress') {
    return <KycOutcome state={state} reason={data.reason} />
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-5">
        <Alert tone="info" title={t('verify.checkFailedTitle')}>
          {t('verify.checkFailedBody')}
        </Alert>
        <Button onClick={() => window.location.reload()}>{t('verify.refresh')}</Button>
      </div>
    )
  }

  if (gaveUp) {
    // The webhook is late, which is ours to chase and not a reason to park the
    // applicant on a spinner or send them back to re-verify. Their documents are
    // in; continuing is the honest next step and the decision lands behind them.
    return (
      <div className="flex flex-col gap-5">
        <Alert tone="info" title={t('verify.stillProcessingTitle')}>
          <p>{t('verify.stillProcessingBody')}</p>
        </Alert>
        <Button onClick={() => navigate('/confirm', { replace: true })}>
          {t('kyc.continueAnyway')}
        </Button>
        <Button variant="ghost" onClick={() => navigate('/identity', { replace: true })}>
          {t('verify.back')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-5 py-10 text-center">
      <div
        className="h-10 w-10 animate-spin rounded-full border-2 border-line border-t-brand"
        role="status"
        aria-label={t('verify.checkingAria')}
      />
      <div>
        <h1 className="text-lg font-bold text-brand">{t('verify.checkingTitle')}</h1>
        <p className="mt-1 text-sm text-muted">{t('verify.checkingBody')}</p>
      </div>
    </div>
  )
}

/** The second device in a QR handoff: it did the camera work without a session. */
function isHandoff(error: unknown): boolean {
  return error instanceof ApiError && error.code === ErrorCode.SESSION_REQUIRED
}

/**
 * The end of the road for the phone in a QR handoff.
 *
 * Deliberately terminal: no button, no link back into the funnel. The rest of
 * the application lives on the desktop that has the session, and giving this
 * device somewhere to go would only start a second application against the
 * same person. Saying "you can close this" is the whole job — the alternative
 * is a user who sits watching a phone while the desktop waits for them.
 */
function HandoffComplete() {
  const site = useSite()
  const { t } = useTranslate()

  return (
    <div className="flex flex-col items-center gap-5 py-10 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-brand/10">
        <svg
          className="h-7 w-7 text-brand"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
      </div>

      <div>
        <h1 className="text-xl font-bold text-brand">{t('handoff.receivedTitle')}</h1>
        <p className="mt-2 text-sm text-muted">{t('handoff.receivedBody')}</p>
      </div>

      <div className="w-full rounded-xl border border-line bg-white p-5 text-sm">
        <p className="font-semibold text-ink">{t('handoff.goBackTitle')}</p>
        <p className="mt-1 text-muted">{t('handoff.goBackBody')}</p>
      </div>

      <p className="text-sm text-muted">{t('handoff.closeTab')}</p>

      <p className="text-xs text-muted">
        {t('handoff.somethingWrong')}{' '}
        <a className="underline" href={`mailto:${site.supportEmail}`}>
          {site.supportEmail}
        </a>
      </p>
    </div>
  )
}
